import { useEffect, useReducer, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { LoaderCircle, Map as MapIcon, Maximize2, RotateCcw } from 'lucide-react';
import type { Id } from '@contracts/common';
import {
  INGEST_RETRYABLE, noticeMessage, type BatchDetailDTO, type IngestJobDTO,
} from '@contracts/ingest';
import { Button } from '../../../components/ui/button';
import { useNarrowView } from '../../../lib/media';
import { cn } from '../../../lib/utils';
import { conversationKey, CONVERSATIONS_KEY, useOwnAppearance } from '../api';
import { useAssistant } from '../AssistantProvider';
import type { Emotion } from '../emotion';
import { addressOf } from '../greeting';
import { Hana } from '../Hana';
import { AI_BUBBLE, SUGGESTION_CHIP } from '../MessageList';
import {
  batchKey, discardBatch, isReading, putParent, retryBatch, runBatch, startIngest, useBatch,
} from './api';
import { BatchFooter } from './BatchFooter';
import { BatchRows } from './BatchRows';
import { FileTypeIcon } from './FileTypeIcon';
import { appliedLine, useBatchWrites } from './useBatchWrites';
import { settleWrites } from './useRowWriter';
import { batchTitle, countsLine, leadSentence, rowTotal, stateLine } from './labels';

const MUTED = 'text-xs text-[hsl(var(--h-muted-foreground))]';
/** D127: a batch the panel can no longer show, as a quiet line in the flow. */
const GONE_LINE = 'mr-auto max-w-[92%] rounded-[9px] border border-dashed border-[hsl(var(--h-border))] px-2 py-[5px] '
  + 'text-xs text-[hsl(var(--h-muted-foreground))]';
const RETRYABLE: readonly string[] = INGEST_RETRYABLE;
const fmt = (n: number) => n.toLocaleString('vi-VN');
const UNDONE = 'Đã hoàn tác';

/** D127: a `batch` message whose job is gone (purged, or a 404 on read). */
export function GoneBatch() {
  return <p data-batch-gone="" className={GONE_LINE}>Lô này không còn</p>;
}

/** D118: what the person sent, drawn on their side of the flow from the job's documents (nothing is stored). */
function FileLine({ job }: { job: IngestJobDTO }) {
  const first = job.documents[0];
  const title = batchTitle(job);
  const mediaType = job.sourceKind === 'text' ? 'text/plain' : first?.mediaType ?? 'application/octet-stream';
  const pages = job.sourceKind === 'file' && first?.pageCount != null ? `${first.pageCount} trang` : null;
  return (
    <div data-batch-file=""
      className="ml-auto flex min-w-0 max-w-[85%] items-center gap-2 rounded-lg border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] px-2.5 py-1.5">
      <FileTypeIcon mediaType={mediaType} filename={first?.filename} />
      <div className="min-w-0 leading-tight">
        <p className="truncate text-sm font-semibold text-[hsl(var(--h-card-foreground))]" title={title}>{title}</p>
        {pages !== null && <p className={MUTED}>{pages}</p>}
      </div>
    </div>
  );
}

function faceOf(job: IngestJobDTO, undone = false): Emotion {
  if (job.held) return 'ask';
  if (isReading(job)) return 'think';
  switch (job.state) {
    case 'ready': return 'ask';
    case 'failed': return 'sorry';
    // N7: an undone batch is no success to be proud of.
    case 'applied': return undone ? 'idle' : 'proud';
    default: return 'idle';
  }
}

/**
 * The card of one ingest job in the panel (spec §8.1, mockup §1 `.batch`), drawn from `GET /batches/:id` and asked
 * again every 2 s while the job reads. T2.3 draws the reading, held, failed and settled states and the ready shell
 * (head, counts, notices, the next range of a split sheet); the rows and the footer come in T3.1/T3.2.
 */
export function BatchCard({ jobId, bare = false }: {
  jobId: Id;
  /** On the batch's own page (§8.2): the card alone, without what the person sent and Hana's line above it. */
  bare?: boolean;
}) {
  const appearance = useOwnAppearance();
  const { prefs, principal, feel, still, conversationId, setOpen, hanaUndo } = useAssistant();
  const queryClient = useQueryClient();
  const { data, error } = useBatch(jobId);
  const { busy, write, show, verb, cancel, apply, undo, changed, expired } = useBatchWrites(jobId);
  const narrow = useNarrowView();
  // §8.5: a batch past its `expiresAt` is over even while the server still lists it `ready` (the sweep comes later),
  // and a 410 on Duyệt says so whatever the clocks say.
  const served = data?.job;
  const lapsed = served !== undefined && served.state === 'ready'
    && (expired || (served.expiresAt !== null && Date.parse(served.expiresAt) <= Date.now()));
  const job = lapsed ? { ...served, state: 'expired' as const } : served;
  const reading = isReading(job);

  // Hana thinks while a card on screen reads (§8.1); the card leaving the screen ends it too.
  useEffect(() => {
    if (!reading) return undefined;
    feel({ kind: 'reading', jobId, active: true });
    return () => feel({ kind: 'reading', jobId, active: false });
  }, [reading, feel, jobId]);

  // §8.5: Hoàn tác stays until the undo window ends; the card is drawn again then.
  const [, redraw] = useReducer((n: number) => n + 1, 0);
  const undoUntil = job?.state === 'applied' && job.actionState === 'executed' && job.undoExpiresAt !== null
    ? Date.parse(job.undoExpiresAt) : null;
  const readyUntil = job?.state === 'ready' && job.expiresAt !== null ? Date.parse(job.expiresAt) : null;
  const wake = undoUntil ?? readyUntil;
  useEffect(() => {
    if (wake === null) return undefined;
    const timer = window.setTimeout(redraw, Math.min(Math.max(0, wake - Date.now()) + 50, 2 ** 31 - 1));
    return () => window.clearTimeout(timer);
  }, [wake]);

  if (error?.status === 404) return <GoneBatch />;
  if (job === undefined) {
    if (error != null) return <p role="alert" className={GONE_LINE}>{error.message}</p>;
    return (
      <div data-batch-card={jobId} aria-busy="true"
        className="h-14 animate-pulse rounded-[10px] border border-[hsl(var(--h-border))] bg-[hsl(var(--h-muted)/0.4)] motion-reduce:animate-none" />
    );
  }

  const address = addressOf(prefs, principal.name);
  const ready = job.state === 'ready' || job.state === 'applied';
  const total = rowTotal(job.counts.byKind);
  const counts = ready ? countsLine(job.counts.byKind) : '';
  // D107: an applied range still offers the next one.
  const nextRange = ready ? job.nextRange : null;
  const document = job.documents[0];
  // D107: the next range goes to the conversation that holds this card.
  const cid = job.conversationId ?? conversationId;

  let body: ReactNode = null;
  if (job.held && (job.state === 'queued' || job.state === 'reading' || job.state === 'proposing')) {
    // §7.7, D126: a big job waits on "Chạy"; "còn Y" only when the server knows the quota left.
    const estimate = `Ước tính khoảng ${fmt(job.estimateTokens ?? 0)} token`;
    body = (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
        <p className="min-w-0 flex-1 text-sm text-[hsl(var(--h-card-foreground))]">
          {job.quotaLeft === null ? estimate : `${estimate}, còn ${fmt(job.quotaLeft)} token hôm nay`}
        </p>
        <Button size="sm" disabled={busy} onClick={() => verb('run', runBatch, job)}>Chạy</Button>
      </div>
    );
  } else if (reading) {
    body = (
      <div className="flex items-center gap-2 px-3 py-2.5">
        <LoaderCircle className="size-4 shrink-0 animate-spin text-[hsl(var(--h-primary))] motion-reduce:animate-none" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-[hsl(var(--h-muted-foreground))]">{/* The stage is written once a unit is done: before that a picked job is already reading. */}
          {job.stage?.label ?? (job.state === 'queued' ? 'Đang chờ đọc…' : 'Đang đọc…')}</p>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => cancel(job)}>Hủy</Button>
      </div>
    );
  } else if (job.state === 'failed') {
    body = (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
        <p role="status" className="min-w-0 flex-1 text-sm text-[hsl(var(--h-error-fg))]">{job.errorMessage}</p>
        {job.errorCode !== null && RETRYABLE.includes(job.errorCode) && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => verb('retry', retryBatch, job)}>
            <RotateCcw aria-hidden="true" />Thử lại
          </Button>
        )}
      </div>
    );
  } else if (job.state === 'ready' && data !== undefined && data.rows.items.length > 0) {
    // D152: "Đặt dưới" after the rows' own writes settle, at the version they left; the rows are judged again under it.
    const place = {
      busy,
      to: (parentItemId: Id | null) => write(`parent:${parentItemId ?? ''}`, async (rid) => {
        await settleWrites(jobId);
        await queryClient.refetchQueries({ queryKey: batchKey(jobId), exact: true });
        const fresh = queryClient.getQueryData<BatchDetailDTO>(batchKey(jobId))?.job ?? job;
        return putParent(jobId, { version: fresh.version, parentItemId }, rid);
      }, (next) => {
        show(next);
        void queryClient.invalidateQueries({ queryKey: batchKey(jobId) });
      }),
    };
    body = <BatchRows job={job} rows={data.rows.items} changed={changed} place={place} />;
  } else if (job.state === 'cancelled' || job.state === 'discarded' || job.state === 'expired') {
    body = <p className={cn(MUTED, 'px-3 py-2.5')}>{stateLine(job.state)}</p>;
  }

  const notices = job.state === 'ready' ? job.notices : [];
  const drafts = (job.counts.byKind.task ?? 0) + (job.counts.byKind.note ?? 0) > 0;
  const notes = notices.length > 0 ? (
    <div className="flex flex-col gap-1.5 border-t border-[hsl(var(--h-border))] px-3 py-2.5">
      {notices.map((n, i) => (
        <p key={i} className="text-xs text-[hsl(var(--h-warning-fg))]">{noticeMessage(n, address)}</p>
      ))}
    </div>
  ) : null;
  const footer = job.state === 'ready' && data !== undefined && data.rows.items.length > 0 ? (
    <BatchFooter job={job} busy={busy} onApply={apply} onDiscard={() => verb('discard', discardBatch, job)}
      extra={drafts ? (
        // §10.1: the tasks and notes as drafts on the Map (D150 `?batch`); on a phone the panel covers it, so it closes.
        <Link to={`/workplace?view=map&batch=${job.id}`} onClick={() => { if (narrow) setOpen(false); }}
          className="inline-flex min-h-11 items-center gap-1 rounded-md text-xs font-semibold text-[hsl(var(--h-primary))] hover:underline lg:min-h-0">
          <MapIcon className="size-3.5 shrink-0" aria-hidden="true" />Xem trên bản đồ
        </Link>
      ) : undefined} />
  ) : null;
  const tail = nextRange !== null && document !== undefined ? (
    <div className="border-t border-[hsl(var(--h-border))] px-3 py-2.5">
        <div>
          <button type="button" disabled={busy}
            onClick={() => write('next', (rid) => startIngest({
              documentId: document.id, rowRange: { from: nextRange[0], to: nextRange[1] },
              ...(job.mapping === null ? {} : { mapping: job.mapping }),
              ...(cid === null ? {} : { conversationId: cid }),
            }, rid), (created) => {
              void queryClient.invalidateQueries({ queryKey: conversationKey(created.conversationId) });
              void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
              void queryClient.invalidateQueries({ queryKey: batchKey(jobId) });
            })}
            className={`${SUGGESTION_CHIP} inline-flex items-center font-medium disabled:cursor-default disabled:opacity-50`}>
            {`Tạo lô tiếp: dòng ${nextRange[0]}–${nextRange[1]}`}
          </button>
        </div>
    </div>
  ) : null;

  // §8.5, D102: the applied card is one line; Hoàn tác while the action stands and its window is open.
  const actionId = job.actionId;
  const undone = actionId !== null && (job.actionState === 'undone' || hanaUndo.undone.has(actionId));
  const canUndo = actionId !== null && !undone && job.actionState === 'executed' && !hanaUndo.gone.has(actionId)
    && undoUntil !== null && undoUntil > Date.now();
  const origin = job.externalOrigin === null ? null
    : `Nguồn: ${job.externalOrigin.serverName} · ${job.externalOrigin.filename}`;
  const head = job.state === 'applied' ? (
    <div data-batch-applied="" className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2">
      <Hana appearance={appearance} emotion={faceOf(job, undone)} size={24} still={still} />
      {/* N7: once undone, no ✓ and no weight: the line only says what was written. */}
      <p className={undone ? 'min-w-0 flex-1 text-sm text-[hsl(var(--h-muted-foreground))]' : 'min-w-0 flex-1 text-sm font-semibold text-[hsl(var(--h-card-foreground))]'}>
        {undone ? appliedLine(job) : `✓ ${appliedLine(job)}`}
      </p>
      {undone ? <span className={MUTED}>{UNDONE}</span> : canUndo && (
        <Button size="sm" variant="outline" onClick={() => undo(actionId, job)}>Hoàn tác</Button>
      )}
      {origin !== null && (
        // S6 §9 (G3 F4): the applied head names the outside system and the file too, full width under the ✓ line.
        <p data-batch-origin="" className={cn(MUTED, 'w-full basis-full truncate')} title={origin}>{origin}</p>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-2 px-3 py-2.5">
      <Hana appearance={appearance} emotion={faceOf(job)} size={24} still={still} />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-bold text-[hsl(var(--h-card-foreground))]" title={batchTitle(job)}>
          {batchTitle(job)}
        </p>
        {counts !== '' && <p className={MUTED}>{counts}</p>}
        {origin !== null && (
          // S6 §9: a batch `ingest.from_external` made names the outside system and the file, under the counts.
          <p data-batch-origin="" className={cn(MUTED, 'truncate')} title={origin}>{origin}</p>
        )}
      </div>
      {job.state === 'ready' && !bare && (
        // §8.1 ⤢: the whole batch on its own page; on a phone the panel covers the page, so it closes first.
        <Link to={`/assistant/batches/${job.id}`} aria-label="Mở trang lô" title="Mở trang lô"
          onClick={() => { if (narrow) setOpen(false); }}
          className="-my-2 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-md text-[hsl(var(--h-muted-foreground))] transition-colors hover:bg-[hsl(var(--h-muted))] hover:text-[hsl(var(--h-primary))] lg:-my-1 lg:-mr-1 lg:size-7">
          <Maximize2 className="size-4" aria-hidden="true" />
        </Link>
      )}
    </div>
  );

  // N6, D149: the step the status line says names the batch, since a conversation can hold several cards (failed has
  // its own role="status").
  const step = reading ? (job.stage?.label ?? (job.state === 'queued' ? 'Đang chờ đọc…' : 'Đang đọc…'))
    : job.state === 'ready' ? `đã đọc xong, ${counts}`
      : job.state === 'applied' ? (undone ? UNDONE : appliedLine(job)) : null;
  const announce = step === null ? '' : `Lô ${batchTitle(job)}: ${step}`;

  return (
    <div className="flex flex-col gap-2">
      {/* L3: an outside file (S6 §9) was not sent by the person, so it has no bubble on their side. */}
      {!bare && job.externalOrigin === null && <FileLine job={job} />}
      {!bare && job.state === 'ready' && total > 0 && (
        <p className={AI_BUBBLE}>{leadSentence(prefs.selfName, total, address, job.counts.included)}</p>
      )}
      {/* D149: the conversation log is the one live announcer. The card's own churn (rows, footer) stays silent; this
          line, outside it and always there, is what the log reads when the card moves on a step. */}
      <p className="sr-only" data-batch-status={jobId}>{announce}</p>
      {/* tabIndex -1: "Mở" in "Lô chờ duyệt" lands the focus here (D120), never in the tab order. */}
      <section data-batch-card={jobId} data-state={job.state} aria-label={`Lô ${batchTitle(job)}`} tabIndex={-1}
        aria-live="off"
        className="min-w-0 outline-none focus-visible:outline-2 focus-visible:outline-[hsl(var(--h-primary))] overflow-hidden rounded-[10px] border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))]">
        {head}
        {body !== null && <div className="border-t border-[hsl(var(--h-border))]">{body}</div>}
        {notes}
        {footer}
        {tail}
      </section>
    </div>
  );
}
